/**
 * @file RegionOption.tsx
 *
 * One recursive row in the region dropdown tree (the "Everywhere" sentinel, a
 * continent, country, or state): a Gravity condensed `Checkbox` + label, plus an
 * expand/collapse caret when the row has children. Clicking the row toggles its
 * selection; clicking the caret toggles its children (the checkbox stops its own
 * click from bubbling so it doesn't double-toggle). Mirrors WSB-v2's RegionOption.
 */

import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { Checkbox, Icon } from '@group.one/react-gravity';
import type { ShippingZoneLocation } from '../../api/shop-types';
import { getLocationKey } from '../shipping-region-utils';
import styles from './RegionSelector.module.css';

interface RegionOptionProps {
  location: ShippingZoneLocation;
  label: string;
  /** Identity keys of the currently selected locations. */
  selectedKeys: Set<string>;
  /** Force the row expanded (e.g. while a search query is active). */
  expanded?: boolean;
  onToggle: (location: ShippingZoneLocation, checked: boolean) => void;
  /** A11y labels for the expand/collapse caret (only when the row has children). */
  expandLabel?: string;
  collapseLabel?: string;
  renderChildren?: () => ReactNode;
}

/**
 * Renders one region row with its checkbox and optional expandable children.
 *
 * @param props - The location, label, selected keys, toggle handler, and children.
 *
 * @returns The region row element.
 */
export function RegionOption({
  location,
  label,
  selectedKeys,
  expanded = false,
  onToggle,
  expandLabel,
  collapseLabel,
  renderChildren,
}: RegionOptionProps) {
  const selected = selectedKeys.has(getLocationKey(location));
  const [isExpanded, setIsExpanded] = useState(selected || expanded);
  const hasChildren = Boolean(renderChildren);

  useEffect(() => {
    if (expanded) {
      setIsExpanded(true);
    }
  }, [expanded]);

  const handleExpand = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setIsExpanded((value) => !value);
  };

  return (
    <div>
      <div
        className={`gv-flex gv-justify-between ${styles.regionOption}`}
        role="button"
        data-id={getLocationKey(location)}
        onClick={() => onToggle(location, !selected)}
      >
        <span className="gv-flex gv-gap-md gv-items-center">
          <span className={styles.checkbox} onClick={(event) => event.stopPropagation()}>
            <Checkbox
              condensed
              checked={selected}
              onChange={(checked) => onToggle(location, checked)}
            />
          </span>
          <span className="gv-text-sm">{label}</span>
        </span>

        {hasChildren && (
          <button
            type="button"
            aria-label={(isExpanded ? collapseLabel : expandLabel) ?? label}
            aria-expanded={isExpanded}
            onClick={handleExpand}
          >
            <Icon name={isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size="small" />
          </button>
        )}
      </div>
      {isExpanded && renderChildren?.()}
    </div>
  );
}
